import mapboxgl from 'mapbox-gl';

import { colorVariables } from './colorVars.js';
import { cityBounds } from './boundaries/city-boundaries.js';
import { assemblyBounds } from './boundaries/assembly-boundaries.js';
import { congressionalBounds } from './boundaries/congressional-boundaries.js';
import { senateBounds } from './boundaries/senate-boundaries.js';
import { supervisorsBounds } from './boundaries/supervisors-boundaries.js';

import { getBoundaries } from './getBoundaries.js';
import {
  CITY_TILESET_ID,
  CITY_LAYER,
  SUPERVISORS_TILESET_ID,
  SUPERVISORS_LAYER,
  ASSEMBLY_TILESET_ID,
  ASSEMBLY_LAYER,
  STATE_SENATE_TILESET_ID,
  STATE_SENATE_LAYER,
  CONGRESSIONAL_TILESET_ID,
  CONGRESSIONAL_LAYER,
} from './constants.js';

let cityColor = colorVariables('city');
let countyColor = colorVariables('county');
let assemblyColor = colorVariables('state');
let congressionalColor = colorVariables('fed');
let senateColor = colorVariables('state-senate');

mapboxgl.accessToken =
  'pk.eyJ1IjoibGF0aW1lcyIsImEiOiJjajhvcXRraGUwNnlwMzNyczR3cTBsaWh1In0.0cPKLwe2A0ET4P5CtWSiLQ';

// CURRENT BOUNDARIES
mapboxgl.clearStorage();

// // mobile check
let screenWidth = window.innerWidth;
let breakpoint = 767;
let statePadding;
let snapshotPadding;
let stroke;
let opacity = 1;
if (screenWidth >= breakpoint) {
  statePadding = { top: 50, right: 40, bottom: 50, left: 40 };
  snapshotPadding = { top: 30, right: 10, bottom: 10, left: 10 };
  stroke = 2;
} else {
  statePadding = { top: 10, right: 20, bottom: 150, left: 20 };
  snapshotPadding = { top: 25, right: 5, bottom: 5, left: 5 };
  stroke = 1.5;
}

// map
export function createMap(
  coordinates,
  city,
  county,
  assembly,
  senate,
  congressional
) {
  // console.log(
  //   'coordinates',
  //   coordinates,
  //   'city',
  //   city,
  //   'county',
  //   county,
  //   'assembly',
  //   assembly,
  //   'senate',
  //   senate,
  //   'congressional',
  //   congressional
  // );

  var map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/latimes/cl7bdawhr000414lmimst3qmf', // From style page
    pitchWithRotate: false, // this prevents accidentially changing the pitch
    center: coordinates, // starting position [lng, lat]
    maxZoom: 9,
    minZoom: 6,
    zoom: 3,
    interactive: false,
    preserveDrawingBuffer: true,
  });

  //add layers
  map.on('load', () => {
    // CONGRESSIONAL
    map.addSource(CONGRESSIONAL_LAYER, {
      type: 'vector',
      url: `mapbox://${CONGRESSIONAL_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'congressional-layer',
        'type': 'line',
        'source': CONGRESSIONAL_LAYER,
        'source-layer': CONGRESSIONAL_LAYER,
        'paint': {
          'line-color': congressionalColor,
          'line-width': stroke,
          'line-opacity': opacity,
        },
        'filter': ['==', 'id', congressional],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'congressional-data-layer',
        'type': 'fill',
        'source': CONGRESSIONAL_LAYER,
        'source-layer': CONGRESSIONAL_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', congressional],
      },
      'water-point-label'
    );

    // ASSEMBLY //

    map.addSource(ASSEMBLY_LAYER, {
      type: 'vector',
      url: `mapbox://${ASSEMBLY_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'assembly-layer',
        'type': 'line',
        'source': ASSEMBLY_LAYER,
        'source-layer': ASSEMBLY_LAYER,
        'paint': {
          'line-color': assemblyColor,
          'line-width': stroke,
          'line-opacity': opacity,
        },
        'filter': ['==', 'id', assembly],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'assembly-data-layer',
        'type': 'fill',
        'source': ASSEMBLY_LAYER,
        'source-layer': ASSEMBLY_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', assembly],
      },
      'water-point-label'
    );

    // SENATE //

    map.addSource(STATE_SENATE_LAYER, {
      type: 'vector',
      url: `mapbox://${STATE_SENATE_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'senate-layer',
        'type': 'line',
        'source': STATE_SENATE_LAYER,
        'source-layer': STATE_SENATE_LAYER,
        'paint': {
          'line-color': senateColor,
          'line-width': stroke,
          'line-opacity': opacity,
        },
        'filter': ['==', 'id', senate],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'senate-data-layer',
        'type': 'fill',
        'source': STATE_SENATE_LAYER,
        'source-layer': STATE_SENATE_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', senate],
      },
      'water-point-label'
    );

    // COUNTY //

    map.addSource(SUPERVISORS_LAYER, {
      type: 'vector',
      url: `mapbox://${SUPERVISORS_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'county-layer',
        'type': 'line',
        'source': SUPERVISORS_LAYER,
        'source-layer': SUPERVISORS_LAYER,
        'paint': {
          'line-color': countyColor,
          'line-width': stroke,
          'line-opacity': opacity,
        },
        'filter': ['==', 'id', county],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'county-data-layer',
        'type': 'fill',
        'source': SUPERVISORS_LAYER,
        'source-layer': SUPERVISORS_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', county],
      },
      'water-point-label'
    );

    //////////////
    //// CITY ////
    //////////////
    map.addSource(CITY_LAYER, {
      type: 'vector',
      url: `mapbox://${CITY_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'city-layer',
        'type': 'line',
        'source': CITY_LAYER,
        'source-layer': CITY_LAYER,
        'paint': {
          'line-color': cityColor,
          'line-width': stroke,
          'line-opacity': opacity,
        },
        'filter': ['==', 'id', city],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'city-data-layer',
        'type': 'fill',
        'source': CITY_LAYER,
        'source-layer': CITY_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', city],
      },
      'water-point-label'
    );
  });

  map.on('idle', function () {
    map.resize();
  });

  //using the same marker as the static api maps
  //https://docs.mapbox.com/mapbox-gl-js/api/markers/
  // const marker = new mapboxgl.Marker({
  //   color: '#000000',
  // })
  //   .setLngLat(coordinates)
  //   .addTo(map);

  var bboxArray = [];

  if (cityBounds(city)) {
    bboxArray.push(cityBounds(city));
  }
  if (supervisorsBounds(county)) {
    bboxArray.push(supervisorsBounds(county));
  }
  if (assemblyBounds(Number(assembly))) {
    bboxArray.push(assemblyBounds(Number(assembly)));
  }
  if (senateBounds(Number(senate))) {
    bboxArray.push(senateBounds(Number(senate)));
  }
  if (congressionalBounds(Number(congressional))) {
    bboxArray.push(congressionalBounds(Number(congressional)));
  }

  let totalBounds = getBoundaries(bboxArray);
  //console.log(totalBounds, snapshotPadding);
  map.fitBounds(totalBounds, {
    padding: snapshotPadding,
  });
}

export function createAssemblySenateMap(coordinates, assembly, senate) {
  var map = new mapboxgl.Map({
    container: 'assembly-senate',
    style: 'mapbox://styles/latimes/clc85okov000a14jy545d5ly3', // From style page
    pitchWithRotate: false, // this prevents accidentially changing the pitch
    center: coordinates, // starting position [lng, lat]
    maxZoom: 9,
    minZoom: 6,
    zoom: 6,
    fitBoundsOptions: {
      padding: statePadding,
    },
    interactive: false,
    preserveDrawingBuffer: true,
  });

  //add layers
  map.on('load', () => {
    // ASSEMBLY //
    map.addSource(ASSEMBLY_LAYER, {
      type: 'vector',
      url: `mapbox://${ASSEMBLY_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'assembly-layer',
        'type': 'line',
        'source': ASSEMBLY_LAYER,
        'source-layer': ASSEMBLY_LAYER,
        'paint': {
          'line-color': assemblyColor,
          'line-width': 2,
          'line-opacity': 1,
        },
        'filter': ['==', 'id', assembly],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'assembly-data-layer',
        'type': 'fill',
        'source': ASSEMBLY_LAYER,
        'source-layer': ASSEMBLY_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', assembly],
      },
      'water-point-label'
    );

    // SENATE //

    map.addSource(STATE_SENATE_LAYER, {
      type: 'vector',
      url: `mapbox://${STATE_SENATE_TILESET_ID}`,
      generateId: true,
    });

    map.addLayer(
      {
        'id': 'senate-layer',
        'type': 'line',
        'source': STATE_SENATE_LAYER,
        'source-layer': STATE_SENATE_LAYER,
        'paint': {
          'line-color': senateColor,
          'line-width': 2,
          'line-opacity': 1,
        },
        'filter': ['==', 'id', senate],
      },
      'water-point-label'
    );

    map.addLayer(
      {
        'id': 'senate-data-layer',
        'type': 'fill',
        'source': STATE_SENATE_LAYER,
        'source-layer': STATE_SENATE_LAYER,
        'paint': {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
        'filter': ['==', 'id', senate],
      },
      'water-point-label'
    );
  });
  //using the same marker as the static api maps
  //https://docs.mapbox.com/mapbox-gl-js/api/markers/
  const marker = new mapboxgl.Marker({
    color: '#000000',
  })
    .setLngLat(coordinates)
    .addTo(map);

  var bboxArray = [];

  if (assemblyBounds(Number(assembly))) {
    bboxArray.push(assemblyBounds(Number(assembly)));
  }
  if (senateBounds(Number(senate))) {
    bboxArray.push(senateBounds(Number(senate)));
  }

  let totalBounds = getBoundaries(bboxArray);
  map.fitBounds(totalBounds, {
    padding: statePadding,
  });
}

function createImage() {
  let comboMap = document.querySelector(
    '#map div.mapboxgl-canvas-container canvas.mapboxgl-canvas'
  );
  // let img = new Image();
  let mapContent = comboMap.toDataURL('image/png', 1.0);
  generateCanvas(mapContent);
}

export function takeScreenshot(map) {
  createImage(map);
}

function generateCanvas(dataurl) {
  // find out what frame to use
  var frameName;
  if (screenWidth >= breakpoint) {
    frameName = window.WM_D;
  } else {
    frameName = window.WM_M;
  }

  var imgCanvas = document.getElementById('result');
  var ctx = imgCanvas.getContext('2d');

  // noted this out to later set these
  // using the mapbox canvas dimensions
  // canvas.width = 500;
  // canvas.height = 500;

  // noted b/c we're overriding it anyways
  // if (screenWidth >= breakpoint) {
  //   imgCanvas.width = 580;
  //   imgCanvas.height = 580;
  // } else {
  //   imgCanvas.width = 484;
  //   imgCanvas.height = 829;
  // }

  // wraps image loading in a promise, which is await-able
  var loadImage = (url) =>
    new Promise(function (ok, fail) {
      var image = new Image();
      image.src = url;
      image.onload = () => ok(image);
      image.onerror = fail;
    });

  var innerURL = dataurl;
  var frameURL = frameName;

  var urls = [innerURL, frameURL];
  var loading = urls.map(loadImage);

  var finalImageData;

  async function loadAllTheImages() {
    var values = await Promise.all(loading);
    var inner = values[0];
    var frame = values[1];
    // reset the img canvas width and height
    // to match the image that was downloaded
    imgCanvas.width = inner.width;
    imgCanvas.height = inner.height;
    // draw the map
    ctx.drawImage(inner, 0, 0, imgCanvas.width, imgCanvas.height);
    // add the watermark over the image
    ctx.globalCompositeOperation = 'source-over';
    // place it at the top left
    ctx.drawImage(frame, 0, 0);
    finalImageData = imgCanvas.toDataURL();
    makeImage(finalImageData);
    return inner, frame, finalImageData;
  }
  loadAllTheImages().catch((e) => console.log(e));
}
// DOWNLOAD THE IMAGE
function makeImage(finalImageData) {
  const link = document.createElement('a');
  link.href = finalImageData;
  link.download = 'map.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
